import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { ChangeDetectionStrategy, Component, Input, OnDestroy } from "@angular/core";
import { MissionPlanRoute, MissionPlanRouteSection } from "@dtm-frontend/shared/ui";
import { FunctionUtils, LocalComponentStore, SpatialUtils } from "@dtm-frontend/shared/utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Polygon } from "@turf/helpers";
import { Color, CustomDataSource, Entity, GeoJsonDataSource, PolygonGraphics, Property } from "cesium";
import { Observable, combineLatest, combineLatestWith, from, map, of, switchMap } from "rxjs";
import { CesiumPolygonHierarchy } from "../../models/cesium.models";
import { CesiumMapService } from "../../services/cesium-map.service";

const GROUND_RISK_BUFFER_OPACITY = 0.4;
const GROUND_RISK_BUFFER_FILL_COLOR = Color.fromCssColorString("#df99a6").withAlpha(GROUND_RISK_BUFFER_OPACITY); // $legacy-color-error-200
const SAFETY_AREA_OPACITY = 0.5;
const SAFETY_AREA_FILL_COLOR = Color.fromCssColorString("#ffd54f").withAlpha(SAFETY_AREA_OPACITY); // $legacy-color-primary-300

interface BufferAreasLayerComponentState {
    isShown: boolean;
    route: MissionPlanRoute | undefined;
    isProcessing: boolean;
    isSafetyAreaVisible: boolean;
    isGroundRiskAreaVisible: boolean;
}

@UntilDestroy()
@Component({
    selector: "dtm-map-lib-buffer-areas-layer",
    template: "",
    providers: [LocalComponentStore],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BufferAreasLayerComponent implements OnDestroy {
    @Input() public set isShown(value: BooleanInput) {
        this.localStore.patchState({ isShown: coerceBooleanProperty(value) });
    }

    @Input({ required: true }) public set route(value: MissionPlanRoute | undefined) {
        this.localStore.patchState({ route: value });
    }

    @Input() public set isProcessing(value: BooleanInput) {
        this.localStore.patchState({ isProcessing: coerceBooleanProperty(value) });
    }

    @Input() public set isSafetyAreaVisible(value: BooleanInput) {
        this.localStore.patchState({ isSafetyAreaVisible: coerceBooleanProperty(value) });
    }

    @Input() public set isGroundRiskAreaVisible(value: BooleanInput) {
        this.localStore.patchState({ isGroundRiskAreaVisible: coerceBooleanProperty(value) });
    }

    protected readonly isShown$ = this.localStore.selectByKey("isShown");
    private readonly isSafetyAreaVisible$ = this.localStore.selectByKey("isSafetyAreaVisible");
    private readonly isGroundRiskAreaVisible$ = this.localStore.selectByKey("isGroundRiskAreaVisible");

    private readonly canShowAnyArea$ = combineLatest([
        this.isShown$,
        this.localStore.selectByKey("isProcessing"),
        this.localStore.selectByKey("route"),
    ]).pipe(map(([isShown, isProcessing, route]) => isShown && !isProcessing && !!route));

    protected readonly isSafetyAreaShown$ = combineLatest([this.canShowAnyArea$, this.isSafetyAreaVisible$]).pipe(
        map(([canShowAnyArea, isSafetyAreaShown]) => canShowAnyArea && isSafetyAreaShown)
    );
    protected readonly isGroundRiskBufferShown$ = combineLatest([this.canShowAnyArea$, this.isGroundRiskAreaVisible$]).pipe(
        map(([canShowAnyArea, isGroundRiskBufferShown]) => canShowAnyArea && isGroundRiskBufferShown)
    );

    private readonly cesiumViewer = this.cesiumService.getViewer();
    private readonly safetAreaDataSource = new CustomDataSource();
    private readonly groundRiskBufferDataSource = new CustomDataSource();

    constructor(
        private readonly localStore: LocalComponentStore<BufferAreasLayerComponentState>,
        private readonly cesiumService: CesiumMapService
    ) {
        this.localStore.setState({
            isShown: true,
            route: undefined,
            isProcessing: false,
            isSafetyAreaVisible: false,
            isGroundRiskAreaVisible: false,
        });

        this.initSafetyAreaEntities();
        this.initGroundRiskBufferEntities();
    }

    public ngOnDestroy(): void {
        this.cesiumViewer.dataSources.remove(this.safetAreaDataSource, true);
        this.cesiumViewer.dataSources.remove(this.groundRiskBufferDataSource, true);
    }

    private async getBufferAreaEntitiesFromPolygons(areas: Polygon[], subtractAreas: Polygon[] = []): Promise<CesiumPolygonHierarchy[]> {
        if (areas.length === 0) {
            return [];
        }

        const combinedAreas = SpatialUtils.union(areas);

        if (!combinedAreas) {
            return [];
        }

        const withSubtractedAreas = SpatialUtils.difference([combinedAreas.geometry, ...subtractAreas]);

        if (!withSubtractedAreas) {
            return [];
        }

        const geoJsonData = await GeoJsonDataSource.load(withSubtractedAreas);

        return geoJsonData.entities.values
            .map((entity) => entity.polygon?.hierarchy as unknown as CesiumPolygonHierarchy)
            .filter(FunctionUtils.isTruthy);
    }

    private initBufferAreaEntities(
        areaExtractionPredicate: (section: MissionPlanRouteSection) => (Polygon | undefined)[],
        subtractAreaExtractionPredicate?: (section: MissionPlanRouteSection) => (Polygon | undefined)[]
    ): Observable<CesiumPolygonHierarchy[]> {
        return this.localStore.selectByKey("route").pipe(
            switchMap((route) => {
                if (!route) {
                    return of([]);
                }

                const areas = route.sections.flatMap(areaExtractionPredicate).filter(FunctionUtils.isTruthy);
                const subtractAreas = route.sections.flatMap(subtractAreaExtractionPredicate ?? (() => [])).filter(FunctionUtils.isTruthy);

                return from(this.getBufferAreaEntitiesFromPolygons(areas, subtractAreas));
            })
        );
    }

    private initSafetyAreaEntities() {
        this.cesiumViewer.dataSources.add(this.safetAreaDataSource);
        this.initBufferAreaEntities(
            (section) => [section.flightZone?.safetyArea.volume.area, section.segment?.safetyArea.volume.area],
            (section) => [section.flightZone?.flightArea.volume.area, section.segment?.flightArea.volume.area]
        )
            .pipe(
                combineLatestWith(this.isSafetyAreaShown$),
                map(([entities, isSafetyAreaShown]) => (isSafetyAreaShown ? entities : [])),
                untilDestroyed(this)
            )
            .subscribe((entities) => {
                this.removeRedundantEntities(this.safetAreaDataSource, entities);

                entities.forEach((hierarchy, index) => {
                    const area = this.safetAreaDataSource.entities.getOrCreateEntity(index.toString());
                    area.polygon = {
                        hierarchy,
                        material: SAFETY_AREA_FILL_COLOR,
                        height: 0 as unknown as Property,
                    } as unknown as PolygonGraphics;
                });

                this.refresh();
            });
    }

    private initGroundRiskBufferEntities() {
        this.cesiumViewer.dataSources.add(this.groundRiskBufferDataSource);
        this.initBufferAreaEntities(
            (section) => [section.flightZone?.groundArea, section.segment?.groundArea],
            (section) => [section.flightZone?.safetyArea.volume.area, section.segment?.safetyArea.volume.area]
        )
            .pipe(
                combineLatestWith(this.isGroundRiskBufferShown$),
                map(([entities, isGroundRiskBufferShown]) => (isGroundRiskBufferShown ? entities : [])),
                untilDestroyed(this)
            )
            .subscribe((entities) => {
                this.removeRedundantEntities(this.groundRiskBufferDataSource, entities);

                entities.forEach((hierarchy, index) => {
                    const area = this.groundRiskBufferDataSource.entities.getOrCreateEntity(index.toString());
                    area.polygon = {
                        hierarchy,
                        material: GROUND_RISK_BUFFER_FILL_COLOR,
                        height: 0,
                    } as unknown as PolygonGraphics;
                });

                this.refresh();
            });
    }

    private removeRedundantEntities(dataSource: CustomDataSource, entities: CesiumPolygonHierarchy[]) {
        dataSource.entities.values.slice(entities.length).forEach((entity: Entity) => dataSource.entities.remove(entity));
    }

    private refresh() {
        this.cesiumService.getScene().requestRender();
    }
}
